<script setup lang="ts">
import "@/assets/css/vendors/simplebar.css";
import "@/assets/css/themes/raze.css";
import { useRoute, useRouter } from "vue-router";
import Lucide from "@/components/Base/Lucide";
import { Menu } from "@/components/Base/Headless";
import { useMenuStore } from "@/stores/menu";
import {
  type ProvideForceActiveMenu,
  forceActiveMenu,
  type Route,
  type FormattedMenu,
  nestedMenu,
  linkTo,
  enter,
  leave,
} from "./side-menu";
import { watch, reactive, ref, computed, onMounted, onUnmounted, provide } from "vue";
import SimpleBar from "simplebar";
import QuickSearch from "@/components/QuickSearch";
import SwitchAccount from "@/components/SwitchAccount";
import NotificationsPanel from "@/components/NotificationsPanel";
import ActivitiesPanel from "@/components/ActivitiesPanel";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/stores/auth";
import { useBrandStore } from "@/stores/brand";
import { useI18n } from "vue-i18n";
import Breadcrumb from "@template/components/Breadcrumb.vue";
import ChatHeaderButton from "@/modules/Chat/components/ChatHeaderButton.vue";
import FeesHeaderButton from "@/modules/Fees/components/FeesHeaderButton.vue";
import NotificationsHeaderButton from "@/modules/Notifications/components/NotificationsHeaderButton.vue";

interface Props {
  showSearch?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showSearch: false,
});

const authStore = useAuthStore();
const { appName } = storeToRefs(useBrandStore());
const quickSearch = ref(false);
const setQuickSearch = (value: boolean) => {
  quickSearch.value = value;
};

const switchAccount = ref(false);
const setSwitchAccount = (value: boolean) => {
  switchAccount.value = value;
};

const notificationsPanel = ref(false);
const setNotificationsPanel = (value: boolean) => {
  notificationsPanel.value = value;
};

const activitiesPanel = ref(false);
const setActivitiesPanel = (value: boolean) => {
  activitiesPanel.value = value;
};

const activeMobileMenu = ref(false);
const route: Route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const menuStore = useMenuStore();

const menuForceKey = ref(0);

const formattedMenu = computed(() => {
  const _l = locale.value;
  const _k = menuForceKey.value;
  void _l;
  void _k;
  return nestedMenu(menuStore.value, route, authStore.user);
});

const dynamicBreadcrumb = computed(() => {
  const _l = locale.value;
  const items: Array<{ label: string; to?: string }> = [
    { label: appName.value, to: "/app" },
  ];
  const meta = (route as any)?.meta;
  if (route.path !== "/") {
    let title = typeof meta?.title === "string" ? t(meta.title) : undefined;
    if (!title) {
      const routeName = String((route as any).name || "Pagina");
      title = routeName.charAt(0).toUpperCase() + routeName.slice(1);
    }
    items.push({ label: title as string });
  }
  return items;
});

const scrollableRef = ref<HTMLDivElement>();
const openMenus = reactive<Record<string, boolean>>({
  "menu.section_family": true,
});

provide<ProvideForceActiveMenu>("forceActiveMenu", (pageName: string) => {
  forceActiveMenu(route, pageName);
  menuForceKey.value += 1;
});

const applyForcedMenuFromRoute = () => {
  const fromMeta = [...route.matched]
    .map((r) => r.meta.forceActiveMenu)
    .filter((v): v is string => typeof v === "string")
    .at(-1);

  if (fromMeta) {
    forceActiveMenu(route, fromMeta);
  } else {
    delete route.forceActiveMenu;
  }

  if (fromMeta === "families" || route.matched.some((r) => r.meta.familyWorkspace)) {
    delete openMenus["menu.section_family"];
    openMenus["menu.section_management"] = true;
  }

  menuForceKey.value += 1;
};

const toggleMenu = (menu: FormattedMenu) => {
  const title = menu.title;
  if (openMenus[title]) {
    delete openMenus[title];
  } else {
    openMenus[title] = true;
  }
};

const navigateMenu = (menu: FormattedMenu) => {
  if (!menu.subMenu) {
    activeMobileMenu.value = false;
  }
  linkTo(menu, router);
};

const isMenuOpen = (menu: FormattedMenu) => {
  return !!(openMenus[menu.title] || menu.activeDropdown);
};

const isFullscreen = ref(false);

const syncFullscreenState = () => {
  isFullscreen.value = !!document.fullscreenElement;
};

const toggleFullscreen = async () => {
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await document.documentElement.requestFullscreen();
    }
  } catch {
    /* el navegador pode rexeitar o cambio */
  }
};

watch(
  () => route.fullPath,
  () => {
    applyForcedMenuFromRoute();
    activeMobileMenu.value = false;
  },
  { immediate: true }
);

onMounted(() => {
  if (scrollableRef.value) {
    new SimpleBar(scrollableRef.value);
  }
  syncFullscreenState();
  document.addEventListener("fullscreenchange", syncFullscreenState);
});

onUnmounted(() => {
  document.removeEventListener("fullscreenchange", syncFullscreenState);
});
</script>

<template>
  <div
    :class="[
      'raze',
      'before:content-[\'\'] before:bg-gradient-to-b before:from-slate-100 before:to-slate-50 before:h-screen before:w-full before:fixed before:top-0 dark:before:from-darkmode-800 dark:before:to-darkmode-900',
    ]"
  >
    <div
      :class="[
        'xl:ml-0 shadow-xl transition-[margin] duration-300 xl:shadow-none fixed top-0 left-0 z-50 side-menu group',
        'after:content-[\'\'] after:fixed after:inset-0 after:bg-black/80 after:xl:hidden',
        { 'ml-0 after:block': activeMobileMenu },
        { '-ml-[275px] after:hidden': !activeMobileMenu },
      ]"
    >
      <div
        :class="[
          'fixed ml-[275px] w-10 h-10 items-center justify-center xl:hidden z-50',
          { flex: activeMobileMenu },
          { hidden: !activeMobileMenu },
        ]"
      >
        <a
          href=""
          @click="
            (event) => {
              event.preventDefault();
              activeMobileMenu = false;
            }
          "
          class="mt-5 ml-5"
        >
          <Lucide icon="X" class="w-8 h-8 text-white" />
        </a>
      </div>
      <div
        :class="[
          'bg-gradient-to-b from-theme-1 to-theme-2 z-20 relative w-[275px] xl:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0] overflow-hidden h-screen flex flex-col',
          'after:content-[\'\'] after:absolute after:inset-0 after:-mr-4 after:bg-texture-white after:bg-contain after:bg-fixed after:bg-[center_-20rem] after:bg-no-repeat',
        ]"
      >
        <div
          class="flex-none hidden xl:flex items-center z-10 px-5 h-[65px] w-[275px] overflow-hidden relative"
        >
          <router-link
            to="/app"
            class="flex items-center h-full"
          >
            <img src="/brand/logo.jpg" alt="ANPA" class="h-10 w-10 rounded-full object-cover ring-2 ring-primary/20" />
            <span class="ml-2 font-display text-lg text-primary hidden xl:inline truncate max-w-[160px]" :title="appName">{{ appName }}</span>
          </router-link>
        </div>
        <div
          ref="scrollableRef"
          :class="[
            'w-full h-full z-20 px-5 pt-5 overflow-y-auto overflow-x-hidden pb-3 [-webkit-mask-image:-webkit-linear-gradient(top,rgba(0,0,0,0),black_24px)] [&:-webkit-scrollbar]:w-0 [&:-webkit-scrollbar]:bg-transparent',
            '[&_.simplebar-content]:p-0 [&_.simplebar-content]:pt-1 [&_.simplebar-track.simplebar-vertical]:w-[10px] [&_.simplebar-track.simplebar-vertical]:mr-0.5 [&_.simplebar-track.simplebar-vertical_.simplebar-scrollbar]:before:bg-slate-400/30',
          ]"
        >
          <ul class="scrollable pt-1">
            <template v-for="(menu, menuKey) in formattedMenu">
              <li
                v-if="typeof menu === 'string'"
                class="side-menu__divider"
                :key="'divider-' + menuKey"
              >
                {{ menu }}
              </li>
              <li
                v-else-if="typeof menu === 'object' && menu.type === 'divider'"
                class="side-menu__divider"
                :key="'divider-obj-' + menuKey"
              >
                {{ $t(menu.title) }}
              </li>
              <li v-else :key="menuKey">
                <a
                  href=""
                  :class="[
                    'side-menu__link',
                    { 'side-menu__link--active': menu.active },
                    {
                      'side-menu__link--active-dropdown': menu.activeDropdown,
                    },
                    menu.accent && `side-menu__link--accent-${menu.accent}`,
                  ]"
                  @click="
                    (event: MouseEvent) => {
                      event.preventDefault();
                      if (menu.subMenu) {
                        toggleMenu(menu);
                      } else {
                        navigateMenu(menu);
                      }
                    }
                  "
                >
                  <Lucide :icon="menu.icon as any" class="side-menu__link__icon" />
                  <div class="side-menu__link__title">{{ $t(menu.title) }}</div>
                  <div v-if="menu.badge" class="side-menu__link__badge">
                    {{ menu.badge }}
                  </div>
                  <Lucide
                    v-if="menu.subMenu"
                    icon="ChevronDown"
                    :class="['side-menu__link__chevron', { 'rotate-180': isMenuOpen(menu) }]"
                  />
                </a>
                <Transition @enter="enter" @leave="leave">
                  <ul v-show="menu.subMenu && isMenuOpen(menu)">
                    <li
                      v-for="(subMenu, subMenuKey) in menu.subMenu"
                      :key="subMenuKey"
                    >
                      <a
                        href=""
                        :class="[
                          'side-menu__link',
                          { 'side-menu__link--active': subMenu.active },
                          {
                            'side-menu__link--active-dropdown':
                              subMenu.activeDropdown,
                          },
                        ]"
                        @click="
                          (event: MouseEvent) => {
                            event.preventDefault();
                            if (subMenu.subMenu) {
                              toggleMenu(subMenu);
                            } else {
                              navigateMenu(subMenu);
                            }
                          }
                        "
                      >
                        <Lucide
                          :icon="subMenu.icon as any"
                          class="side-menu__link__icon"
                        />
                        <div class="side-menu__link__title">
                          {{ $t(subMenu.title) }}
                        </div>
                        <div
                          v-if="subMenu.badge"
                          class="side-menu__link__badge"
                        >
                          {{ subMenu.badge }}
                        </div>
                        <Lucide
                          v-if="subMenu.subMenu"
                          icon="ChevronDown"
                          :class="['side-menu__link__chevron', { 'rotate-180': isMenuOpen(subMenu) }]"
                        />
                      </a>
                      <Transition @enter="enter" @leave="leave">
                        <ul v-show="subMenu.subMenu && isMenuOpen(subMenu)">
                          <li
                            v-for="(
                              lastSubMenu, lastSubMenuKey
                            ) in subMenu.subMenu"
                            :key="lastSubMenuKey"
                          >
                            <a
                              href=""
                              :class="[
                                'side-menu__link',
                                {
                                  'side-menu__link--active':
                                    lastSubMenu.active,
                                },
                                {
                                  'side-menu__link--active-dropdown':
                                    lastSubMenu.activeDropdown,
                                },
                              ]"
                              @click="
                                (event: MouseEvent) => {
                                  event.preventDefault();
                                  navigateMenu(lastSubMenu);
                                }
                              "
                            >
                              <Lucide
                                :icon="lastSubMenu.icon as any"
                                class="side-menu__link__icon"
                              />
                              <div class="side-menu__link__title">
                                {{ $t(lastSubMenu.title) }}
                              </div>
                              <div
                                v-if="lastSubMenu.badge"
                                class="side-menu__link__badge"
                              >
                                {{ lastSubMenu.badge }}
                              </div>
                            </a>
                          </li>
                        </ul>
                      </Transition>
                    </li>
                  </ul>
                </Transition>
              </li>
            </template>
          </ul>
        </div>
      </div>
      <div
        :class="[
          'fixed h-[65px] transition-[margin] duration-100 xl:ml-[275px] mt-3.5 inset-x-0 top-0',
          'before:content-[\'\'] before:mx-5 before:absolute before:top-0 before:inset-x-0 before:-mt-[15px] before:h-[20px] before:backdrop-blur',
        ]"
      >
        <div
          class="absolute inset-x-0 h-full mx-5 box before:content-[''] before:z-[-1] before:inset-x-4 before:shadow-sm before:h-full before:bg-slate-50 before:border before:border-slate-200 before:absolute before:rounded-lg before:mx-auto before:top-0 before:mt-3 before:dark:bg-darkmode-600/70 before:dark:border-darkmode-500/60"
        >
          <div class="flex items-center w-full h-full px-5">
            <div class="flex items-center gap-1 xl:hidden">
              <a
                href=""
                @click="
                  (event) => {
                    event.preventDefault();
                    activeMobileMenu = true;
                  }
                "
                class="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-darkmode-400"
              >
                <Lucide icon="AlignJustify" class="w-[18px] h-[18px]" />
              </a>
              <a
                v-if="props.showSearch"
                href=""
                class="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-darkmode-400"
                @click="
                  (event) => {
                    event.preventDefault();
                    quickSearch = true;
                  }
                "
              >
                <Lucide icon="Search" class="w-[18px] h-[18px]" />
              </a>
            </div>
            <Breadcrumb :key="$route.path" :items="dynamicBreadcrumb" class="my-0" />
            <template v-if="props.showSearch">
              <div
                class="relative justify-center flex-1 hidden xl:flex"
                @click="
                  (event) => {
                    event.preventDefault();
                    quickSearch = true;
                  }
                "
              >
                <div
                  class="bg-slate-50 border w-[350px] flex items-center py-2 px-3.5 rounded-[0.5rem] text-slate-400 cursor-pointer hover:bg-slate-100 transition-colors dark:bg-darkmode-800"
                >
                  <Lucide icon="Search" class="w-[18px] h-[18px]" />
                  <div class="ml-2.5 mr-auto">Quick search...</div>
                  <div>⌘K</div>
                </div>
              </div>
              <QuickSearch
                :quickSearch="quickSearch"
                :setQuickSearch="setQuickSearch"
              />
            </template>
            <div class="flex items-center flex-1">
              <div class="flex items-center gap-1 ml-auto">
                <FeesHeaderButton />
                <ChatHeaderButton />
                <NotificationsHeaderButton
                  :open="notificationsPanel"
                  :set-open="setNotificationsPanel"
                />
                <a
                  href=""
                  class="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-darkmode-400"
                  :title="t('header_feed.title')"
                  :aria-label="t('header_feed.title')"
                  @click.prevent="activitiesPanel = true"
                >
                  <Lucide icon="Newspaper" class="w-[18px] h-[18px]" />
                </a>
                <a
                  href=""
                  class="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-darkmode-400"
                  :title="isFullscreen ? $t('common.close') : undefined"
                  @click.prevent="toggleFullscreen"
                >
                  <Lucide
                    :icon="isFullscreen ? 'Minimize2' : 'Expand'"
                    class="w-[18px] h-[18px]"
                  />
                </a>
              </div>
              <Menu class="ml-5">
                <Menu.Button
                  class="flex items-center justify-center overflow-hidden rounded-full w-[36px] h-[36px] border-[3px] border-slate-200/70 bg-slate-100 text-slate-500 dark:bg-darkmode-400 dark:text-slate-400"
                >
                  <Lucide icon="User" class="w-5 h-5" />
                </Menu.Button>
                <Menu.Items class="w-56 mt-1">
                  <Menu.Item class="font-medium text-slate-500 cursor-default hover:bg-transparent">
                    {{ authStore.user?.first_name }} {{ authStore.user?.last_name }}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    @click="
                      () => {
                        router.push({
                          name: 'profile',
                        });
                      }
                    "
                  >
                    <Lucide icon="Users" class="w-4 h-4 mr-2" />
                    {{ $t('menu.profile') }}
                  </Menu.Item>
                  <Menu.Item @click="authStore.logout">
                    <Lucide icon="Power" class="w-4 h-4 mr-2" />
                    {{ $t('menu.logout') }}
                  </Menu.Item>
                </Menu.Items>
              </Menu>
            </div>
            <ActivitiesPanel
              :activitiesPanel="activitiesPanel"
              :setActivitiesPanel="setActivitiesPanel"
            />
            <NotificationsPanel
              :notificationsPanel="notificationsPanel"
              :setNotificationsPanel="setNotificationsPanel"
            />
            <SwitchAccount
              :switchAccount="switchAccount"
              :setSwitchAccount="setSwitchAccount"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      :class="[
        'transition-[margin,width] duration-100 px-5 pt-[56px] pb-16 relative z-20 xl:ml-[275px]',
      ]"
    >
      <div class="container mt-[65px]">
        <RouterView />
      </div>
    </div>
  </div>
</template>
